import { Button } from "./ui/button";

/**
 * Empty state used across every list/table page (Jobs Queue tabs, Impounds,
 * Yard Stock, Impound History, Intake Inbox, NTS Impounds). Consistent visual
 * language so dispatchers instantly recognise "nothing here yet" and, if they
 * can act, what the next step is.
 *
 * Props:
 *   icon      – Lucide icon component (rendered inside a slate-tinted circle)
 *   title     – short one-liner (e.g. "No pending jobs")
 *   hint      – optional secondary sentence explaining why / what happens next
 *   action    – optional { label, onClick, testId } to render a slate CTA
 *   className – extra padding overrides
 *   testId    – outer wrapper data-testid (default "empty-state")
 */
export function EmptyState({ icon: Icon, title, hint, action, className = "", testId = "empty-state" }) {
    return (
        <div
            className={`flex flex-col items-center justify-center text-center py-16 px-6 ${className}`}
            data-testid={testId}
        >
            {Icon && (
                <div className="mb-4 h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <Icon className="h-7 w-7 text-slate-500 dark:text-slate-400" aria-hidden="true" />
                </div>
            )}
            <h3 className="font-display text-base font-bold text-slate-900 dark:text-slate-100">
                {title}
            </h3>
            {hint && (
                <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400 max-w-md">
                    {hint}
                </p>
            )}
            {action && (
                <Button
                    onClick={action.onClick}
                    className="mt-5 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
                    data-testid={action.testId || "empty-state-action"}
                >
                    {action.icon ? <action.icon className="h-4 w-4" aria-hidden="true" /> : null}
                    {action.label}
                </Button>
            )}
        </div>
    );
}

export default EmptyState;
